import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { trailToday } from 'utils/trailTime';
import { ROUTES } from 'constants/RoutePaths';
import { useGetPlanQuery } from 'app/api/planApiSlice';
import TSButton from 'components/common/Button/Button';
import MLModal from 'components/common/Modal/Modal';
import Notice from 'components/common/Notice/Notice';
import StatusBadge from 'components/common/StatusBadge/StatusBadge';
import TSCard from 'components/common/TSCard/TSCard';
import GearList from 'components/TrailSafe/GearList/GearList';
import PlanFacts from 'components/TrailSafe/PlanFacts/PlanFacts';
import PlanGuests from 'components/TrailSafe/PlanGuests/PlanGuests';
import PlanNotes from 'components/TrailSafe/PlanNotes/PlanNotes';
import SharePlan from 'components/TrailSafe/SharePlan/SharePlan';
import EditPlan from './EditPlan';
import CancelPlan from './CancelPlan';

const STATUS = {
  STARTED: ['started', 'success', 'This Plan has started.'],
  CANCELLED: ['cancelled', 'info', 'You cancelled this Plan.'],
  MISSED: [
    'missed',
    'warning',
    'This Plan was missed: its day ended without it starting.',
  ],
};

// One of the walker's Plans: its details, Plan Guests and notes, and its
// Gear List to work through. All can change, and the Plan can be cancelled,
// until it starts. A Plan whose Emergency Contact was deleted says it needs
// a new one, and can't start until it has one.
const PlanDetail = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { data: plan, isLoading, error } = useGetPlanQuery(planId);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  if (isLoading) return <div className="ts-page">Loading...</div>;
  if (error) {
    return (
      <div className="ts-page">
        {error.status === 404 ? 'Plan not found.' : 'Error loading your Plan.'}
      </div>
    );
  }

  const today = trailToday();
  const [badge, tone, statusText] = STATUS[plan.status] ?? [
    plan.planned_date === today ? 'today' : 'planned',
  ];

  return (
    <div className="ts-page ts-page--wide flex flex-col gap-6">
      <header className="flex flex-col gap-3">
        <div>
          <StatusBadge status={badge} />
        </div>
        <Typography variant="h2">
          {plan.trail_name}, {plan.park_name}
        </Typography>
        {statusText && <Notice tone={tone}>{statusText}</Notice>}
      </header>
      {/* On a laptop the Plan's details sit beside its Gear List; on a
          phone it's one column in the same order. */}
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-6">
          <TSCard>
            <PlanFacts plan={plan} today={today} />
            <div className="flex flex-wrap gap-2 mt-2">
              <SharePlan
                shareToken={plan.share_token}
                trailName={plan.trail_name}
              />
              {plan.editable && (
                <>
                  <TSButton
                    btnText={
                      plan.needs_emergency_contact
                        ? 'Choose a contact'
                        : 'Edit Plan'
                    }
                    btnVariant={
                      plan.needs_emergency_contact ? 'contained' : 'outlined'
                    }
                    onClickFunc={() => setIsEditOpen(true)}
                  />
                  <TSButton
                    btnText="Cancel Plan"
                    btnVariant="text"
                    btnColor="error"
                    onClickFunc={() => setIsCancelOpen(true)}
                  />
                </>
              )}
            </div>
          </TSCard>
          <PlanGuests
            planId={plan.plan_id}
            guests={plan.guests}
            editable={plan.editable}
          />
          <PlanNotes
            planId={plan.plan_id}
            notes={plan.notes}
            editable={plan.editable}
          />
        </div>
        <GearList
          planId={plan.plan_id}
          gearList={plan.gear_list}
          editable={plan.editable}
        />
      </div>
      <div>
        <TSButton
          btnText="Back to My Plans"
          btnVariant="text"
          onClickFunc={() => navigate(`/${ROUTES.MY_PLANS}`)}
        />
      </div>
      <MLModal open={isEditOpen} setOpenModal={setIsEditOpen}>
        {isEditOpen && <EditPlan plan={plan} toggleModal={setIsEditOpen} />}
      </MLModal>
      <MLModal open={isCancelOpen} setOpenModal={setIsCancelOpen}>
        {isCancelOpen && (
          <CancelPlan plan={plan} toggleModal={setIsCancelOpen} />
        )}
      </MLModal>
    </div>
  );
};

export default PlanDetail;
